import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { listPlanInstances } from "../plan/plan-api.js";
import {
  MPI_NAME,
  activitiesFromText,
  addVerificationVisit,
  businessDetails,
  currentRegistration,
  listRegistrations,
  listVerificationVisits,
  saveBusinessDetails,
  saveRegistration,
  visitOutcomeLabels,
  visitSummary,
  type Registration,
  type VisitOutcome,
} from "./registration-api.js";

const FIELD_ERROR = "text-sm font-semibold text-red-800";

function formText(form: FormData, key: string): string | null {
  return String(form.get(key) ?? "").trim() || null;
}

function RegistrationForm({
  locationId,
  registration,
}: {
  locationId: string;
  registration: Registration | null;
}) {
  const id = useId();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Registration["authority_kind"]>(
    registration?.authority_kind ?? "council",
  );
  const save = useMutation({
    mutationFn: (form: FormData) =>
      saveRegistration(
        {
          authority_kind: kind,
          authority_name:
            kind === "mpi"
              ? MPI_NAME
              : (formText(form, "authority_name") ?? ""),
          expires_on: formText(form, "expires_on"),
          registered_on: formText(form, "registered_on"),
          registration_no: formText(form, "registration_no"),
        },
        locationId,
        registration,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["registrations"] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registration</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            save.mutate(new FormData(event.currentTarget));
          }}
        >
          <div>
            <Label htmlFor={`${id}-kind`}>Registered with</Label>
            <Select
              id={`${id}-kind`}
              onChange={(event) =>
                setKind(event.target.value as Registration["authority_kind"])
              }
              value={kind}
              wrapperClassName="mt-1.5"
            >
              <option value="council">Council</option>
              <option value="mpi">MPI</option>
            </Select>
          </div>
          {kind === "council" ? (
            <div>
              <Label htmlFor={`${id}-council`}>Council</Label>
              <Input
                className="mt-1.5"
                defaultValue={
                  registration?.authority_kind === "council"
                    ? registration.authority_name
                    : ""
                }
                id={`${id}-council`}
                maxLength={240}
                name="authority_name"
                required
              />
            </div>
          ) : (
            <span className="hidden sm:block" />
          )}
          <div>
            <Label htmlFor={`${id}-number`}>Registration number</Label>
            <Input
              className="mt-1.5"
              defaultValue={registration?.registration_no ?? ""}
              id={`${id}-number`}
              maxLength={200}
              name="registration_no"
            />
          </div>
          <span className="hidden sm:block" />
          <div>
            <Label htmlFor={`${id}-registered`}>Registered on</Label>
            <Input
              className="mt-1.5"
              defaultValue={registration?.registered_on ?? ""}
              id={`${id}-registered`}
              name="registered_on"
              type="date"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-expires`}>Expires on</Label>
            <Input
              className="mt-1.5"
              defaultValue={registration?.expires_on ?? ""}
              id={`${id}-expires`}
              name="expires_on"
              type="date"
            />
          </div>
          {save.error ? (
            <p className={`${FIELD_ERROR} sm:col-span-2`} role="alert">
              {displayError(save.error)}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-3 sm:col-span-2">
            {save.isSuccess ? (
              <span
                className="text-sm font-semibold text-emerald-800"
                role="status"
              >
                Saved
              </span>
            ) : null}
            <Button disabled={save.isPending} type="submit">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function BusinessDetailsForm({ locationId }: { locationId: string }) {
  const id = useId();
  const queryClient = useQueryClient();
  const plans = useQuery({
    queryFn: () => listPlanInstances(locationId),
    queryKey: ["plan-instances", locationId],
  });
  const instances = plans.data ?? [];
  // The published plan is what the report reads.
  const source =
    instances.find((instance) => instance.state === "published") ??
    instances.find((instance) => instance.state === "draft");
  const details = businessDetails(source?.answers ?? {});
  const save = useMutation({
    mutationFn: (form: FormData) =>
      saveBusinessDetails(
        {
          activities: activitiesFromText(String(form.get("activities") ?? "")),
          day_to_day_manager: formText(form, "day_to_day_manager") ?? "",
          operator_name: formText(form, "operator_name") ?? "",
        },
        instances,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["plan-instances", locationId],
      }),
  });

  if (plans.isPending) return <LoadingState label="Loading the plan" />;
  if (plans.error)
    return (
      <ErrorState
        message={displayError(plans.error)}
        onAction={() => void plans.refetch()}
        title="Business details aren’t available"
      />
    );
  if (!source)
    return (
      <EmptyState calm title="Publish your plan to add business details" />
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Business details</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 sm:grid-cols-2"
          key={source.id}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            save.mutate(new FormData(event.currentTarget));
          }}
        >
          <div>
            <Label htmlFor={`${id}-operator`}>Operator</Label>
            <Input
              className="mt-1.5"
              defaultValue={details.operator_name}
              id={`${id}-operator`}
              maxLength={240}
              name="operator_name"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-manager`}>Day-to-day manager</Label>
            <Input
              className="mt-1.5"
              defaultValue={details.day_to_day_manager}
              id={`${id}-manager`}
              maxLength={240}
              name="day_to_day_manager"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-activities`}>Food activities</Label>
            <textarea
              className="mt-1.5 min-h-24 w-full rounded-md border border-stone-300 bg-surface p-3 text-sm"
              defaultValue={details.activities.join("\n")}
              id={`${id}-activities`}
              maxLength={2000}
              name="activities"
            />
          </div>
          {save.error ? (
            <p className={`${FIELD_ERROR} sm:col-span-2`} role="alert">
              {displayError(save.error)}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-3 sm:col-span-2">
            {save.isSuccess ? (
              <span
                className="text-sm font-semibold text-emerald-800"
                role="status"
              >
                Saved
              </span>
            ) : null}
            <Button disabled={save.isPending} type="submit">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function VisitForm({
  locationId,
  registration,
}: {
  locationId: string;
  registration: Registration | null;
}) {
  const id = useId();
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState(0);
  const visits = useQuery({
    queryFn: () => listVerificationVisits(locationId),
    queryKey: ["verification-events", locationId],
  });
  const save = useMutation({
    mutationFn: (form: FormData) =>
      addVerificationVisit(
        {
          next_due_on: formText(form, "next_due_on"),
          occurred_on: String(form.get("occurred_on") ?? ""),
          outcome: (formText(form, "outcome") as VisitOutcome | null) ?? null,
          verifier_name: formText(form, "verifier_name"),
        },
        registration!.id,
        locationId,
      ),
    onSuccess: () => {
      setFormKey((value) => value + 1);
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["verification-events", locationId],
        }),
        // Location.last_verified_on moves with the latest visit.
        queryClient.invalidateQueries({ queryKey: ["locations"] }),
      ]);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Verification visits</CardTitle>
      </CardHeader>
      <CardContent>
        {visits.data && visits.data.length > 0 ? (
          <ul className="mb-5 divide-y divide-stone-200 rounded-lg border border-stone-200">
            {visits.data.map((visit) => (
              <li className="px-3 py-2.5 text-sm" key={visit.id}>
                {visitSummary(visit)}
              </li>
            ))}
          </ul>
        ) : null}
        <form
          className="grid gap-4 sm:grid-cols-2"
          key={formKey}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            save.mutate(new FormData(event.currentTarget));
          }}
        >
          <fieldset
            className="contents"
            disabled={!registration || save.isPending}
          >
            <div>
              <Label htmlFor={`${id}-date`}>Visit date</Label>
              <Input
                className="mt-1.5"
                id={`${id}-date`}
                name="occurred_on"
                required
                type="date"
              />
            </div>
            <div>
              <Label htmlFor={`${id}-verifier`}>Verifier</Label>
              <Input
                className="mt-1.5"
                id={`${id}-verifier`}
                maxLength={200}
                name="verifier_name"
              />
            </div>
            <div>
              <Label htmlFor={`${id}-outcome`}>Outcome</Label>
              <Select
                defaultValue=""
                id={`${id}-outcome`}
                name="outcome"
                wrapperClassName="mt-1.5"
              >
                <option value="">Choose an outcome</option>
                {Object.entries(visitOutcomeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor={`${id}-next`}>Next visit due</Label>
              <Input
                className="mt-1.5"
                id={`${id}-next`}
                name="next_due_on"
                type="date"
              />
            </div>
          </fieldset>
          {save.error ? (
            <p className={`${FIELD_ERROR} sm:col-span-2`} role="alert">
              {displayError(save.error)}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-3 sm:col-span-2">
            {!registration ? (
              <span className="text-sm text-stone-500">
                Save the registration first
              </span>
            ) : save.isSuccess ? (
              <span
                className="text-sm font-semibold text-emerald-800"
                role="status"
              >
                Visit saved
              </span>
            ) : null}
            <Button disabled={!registration || save.isPending} type="submit">
              {save.isPending ? "Saving…" : "Add visit"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// Setup › Registration: what the verifier's report needs on its first page.
export function RegistrationPanel({ locationId }: { locationId: string }) {
  const registrations = useQuery({
    queryFn: listRegistrations,
    queryKey: ["registrations"],
  });
  if (registrations.isPending)
    return <LoadingState label="Loading the registration" />;
  if (registrations.error)
    return (
      <ErrorState
        message={displayError(registrations.error)}
        onAction={() => void registrations.refetch()}
        title="The registration isn’t available"
      />
    );
  const registration = currentRegistration(registrations.data, locationId);
  return (
    <div className="space-y-5">
      <RegistrationForm
        key={locationId}
        locationId={locationId}
        registration={registration}
      />
      <BusinessDetailsForm locationId={locationId} />
      <VisitForm locationId={locationId} registration={registration} />
    </div>
  );
}
